import { Component, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { SsoApplicationsService } from 'src/app/services/sso-applications.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { SsoApplicationFormComponent } from '../sso-application-form/sso-application-form.component';
import { SecretDisplayDialogComponent } from '../../developer-clients/secret-display-dialog/secret-display-dialog.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-sso-applications-list',
  templateUrl: './sso-applications-list.component.html',
  styleUrls: ['./sso-applications-list.component.scss']
})
export class SsoApplicationsListComponent implements OnInit {
  applications: any[] = [];
  page: number = 1;
  count: number = 10;
  total: number = 0;
  isAdmin: boolean = false;

  constructor(
    private ssoService: SsoApplicationsService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.isAdmin = localStorage.getItem('role') === 'admin';
    if (this.isAdmin) {
      this.loadApplications();
    }
  }

  loadApplications(): void {
    this.spinner.show();
    this.ssoService.getApplications().subscribe({
      next: (res: any) => {
        this.applications = Array.isArray(res) ? res : res.response?.data || [];
        this.total = this.applications.length;
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || 'Failed to load SSO applications', 'error');
      }
    });
  }

  openCreateDialog(): void {
    this.dialogService.open(SsoApplicationFormComponent).onClose.subscribe(result => {
      if (result?.saved) {
        this.loadApplications();
      }
    });
  }

  openEditDialog(app: any): void {
    this.dialogService.open(SsoApplicationFormComponent, {
      context: { clientId: app.clientId }
    }).onClose.subscribe(result => {
      if (result?.saved) {
        this.loadApplications();
      }
    });
  }

  rotateSecret(app: any): void {
    Swal.fire({
      title: `Rotate secret for "${app.name}"?`,
      text: 'The existing secret will be invalidated immediately.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'Yes, rotate'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.ssoService.rotateSecret(app.clientId).subscribe({
          next: (res: any) => {
            this.spinner.hide();
            const data = res.response?.data || res;
            const clientId = data.clientId || app.clientId;
            const clientSecret = data.client_secret;
            this.dialogService.open(SecretDisplayDialogComponent, {
              context: { clientId, clientSecret, title: 'SSO Application Secret Rotated' }
            });
            this.loadApplications();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || 'Failed to rotate secret', 'error');
          }
        });
      }
    });
  }

  revokeApplication(app: any): void {
    Swal.fire({
      title: `Revoke "${app.name}"?`,
      text: 'This will deactivate the application and block all SSO access.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, revoke'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.ssoService.revokeApplication(app.clientId).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success('Application revoked');
            this.loadApplications();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || 'Failed to revoke application', 'error');
          }
        });
      }
    });
  }

  activateApplication(app: any): void {
    Swal.fire({
      title: `Activate "${app.name}"?`,
      text: 'This will re-enable SSO access for this application.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      confirmButtonText: 'Yes, activate'
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.ssoService.activateApplication(app.clientId).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success('Application activated');
            this.loadApplications();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire('Error', err.error?.response?.message || 'Failed to activate application', 'error');
          }
        });
      }
    });
  }

  pageChanged(event: number): void {
    this.page = event;
  }

  formatCreatedAt(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  }

  truncateClientId(clientId: string): string {
    if (!clientId) return '-';
    if (clientId.length <= 16) return clientId;
    return clientId.substring(0, 8) + '…' + clientId.substring(clientId.length - 4);
  }

  copyClientId(clientId: string): void {
    navigator.clipboard.writeText(clientId).then(() => {
      this.toastr.success('Client ID copied to clipboard');
    }).catch(() => {
      this.toastr.error('Failed to copy to clipboard');
    });
  }

  isActive(app: any): boolean {
    return app.status === 'active';
  }

  formatRoles(roles: string[]): string[] {
    return roles || [];
  }
}
